import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { ScaleField } from './ScaleField'
import type { Campo } from '../../types'

interface FieldRendererProps {
  campo: Campo
  value: string
  onChange: (value: string) => void
  error?: string
  verticalScale?: boolean
}

export function FieldRenderer({ campo, value, onChange, error, verticalScale }: FieldRendererProps) {
  const renderField = () => {
    switch (campo.tipo) {
      case 'texto':
        return (
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={campo.placeholder}
            maxLength={campo.max_caracteres || undefined}
          />
        )

      case 'email':
        return (
          <Input
            type="email"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={campo.placeholder || 'correo@ejemplo.com'}
          />
        )

      case 'numero':
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={campo.placeholder}
            min={campo.min_valor ?? undefined}
            max={campo.max_valor ?? undefined}
          />
        )

      case 'telefono':
        return (
          <Input
            type="tel"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={campo.placeholder || '0999-999-999'}
          />
        )

      case 'textarea':
        return (
          <div>
            <Textarea
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={campo.placeholder}
              maxLength={campo.max_caracteres || undefined}
              rows={4}
            />
            {campo.max_caracteres && (
              <div className="text-xs text-gray-400 text-right mt-1">
                {value.length}/{campo.max_caracteres}
              </div>
            )}
          </div>
        )

      case 'select':
        return (
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger>
              <SelectValue placeholder={campo.placeholder || 'Seleccionar...'} />
            </SelectTrigger>
            <SelectContent>
              {campo.opciones.map((op) => (
                <SelectItem key={op.id} value={op.valor}>{op.valor}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )

      case 'radio':
        if (verticalScale) {
          // Estilo card vertical como en multi-step Django
          return (
            <div className="space-y-3">
              {campo.opciones.map((op) => {
                const isSelected = value === op.valor
                const displayVal = op.valor.includes('|||') ? op.valor.split('|||')[0].trim() : op.valor
                return (
                  <button
                    key={op.id}
                    type="button"
                    onClick={() => onChange(op.valor)}
                    className={`w-full p-2.5 sm:p-4 border-2 rounded-lg text-left transition-all flex items-center ${
                      isSelected
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-gray-200 bg-white hover:border-blue-400'
                    }`}
                  >
                    <span className={`font-medium text-sm sm:text-base ${isSelected ? 'text-white' : 'text-gray-800'}`}>
                      {displayVal}
                    </span>
                  </button>
                )
              })}
            </div>
          )
        }
        return (
          <RadioGroup value={value} onValueChange={onChange} className="space-y-2">
            {campo.opciones.map((op) => (
              <div key={op.id} className="flex items-center space-x-2">
                <RadioGroupItem value={op.valor} id={`radio-${campo.id}-${op.id}`} />
                <Label htmlFor={`radio-${campo.id}-${op.id}`} className="cursor-pointer font-normal">
                  {op.valor}
                </Label>
              </div>
            ))}
          </RadioGroup>
        )

      case 'checkbox': {
        const selectedValues = value ? value.split('|||').filter(Boolean) : []
        const handleCheckbox = (opcion: string, checked: boolean) => {
          const newValues = checked
            ? [...selectedValues, opcion]
            : selectedValues.filter((v) => v !== opcion)
          onChange(newValues.join('|||'))
        }
        return (
          <div className="space-y-2">
            {campo.opciones.map((op) => (
              <div key={op.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`check-${campo.id}-${op.id}`}
                  checked={selectedValues.includes(op.valor)}
                  onCheckedChange={(checked) => handleCheckbox(op.valor, !!checked)}
                />
                <Label htmlFor={`check-${campo.id}-${op.id}`} className="cursor-pointer font-normal">
                  {op.valor}
                </Label>
              </div>
            ))}
          </div>
        )
      }

      case 'fecha':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        )

      case 'escala':
        return (
          <ScaleField
            min={campo.min_valor ?? 1}
            max={campo.max_valor ?? 5}
            value={value}
            onChange={onChange}
            etiquetas={campo.escala_etiquetas}
            vertical={verticalScale}
          />
        )

      default:
        return (
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={campo.placeholder}
          />
        )
    }
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {campo.etiqueta}
        {campo.requerido && <span className="text-red-500 ml-1">*</span>}
      </label>
      {campo.descripcion && (
        <p className="text-xs text-gray-500">{campo.descripcion}</p>
      )}
      {renderField()}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  )
}
